/*
 * Fly-to-cart animation for product-card quick add.
 *
 * Classic script on purpose: it loads on every cart_type, and the drawer-v3
 * import map (the only way bare-specifier modules resolve) exists only when
 * cart_type == 'drawer-v3'. Three includes load it:
 *   - snippets/tagalys-script-header.liquid, on Tagalys collection pages and
 *     Tagalys search results (one include per branch): full behaviour,
 *     including the document-level product-card click listener below.
 *   - snippets/xgen-marketing-assets.liquid, wherever the XGen
 *     recommendations widget loads (home / PDP recommendation sections):
 *     launcher only — the script tag carries data-fly-cards="off", so the
 *     product-card listener is not bound and product cards on those pages
 *     keep not animating. The widget's add-to-bag handlers call
 *     window.flyToCartLaunch directly instead: its cards render inside a
 *     shadow root, where neither TRIGGER nor the retargeted event.target of
 *     a document listener can reach.
 *   - snippets/native-product-recommendations.liquid, wherever a native
 *     recommendations / recently-viewed block renders (home / PDP / cart
 *     recommendation sections, search no-results): launcher only as well.
 *     Its cards are light DOM and would match TRIGGER, but the listener is
 *     deliberately not bound on those pages, so native-product-recommendations.js
 *     binds an element-scoped copy of onClick's checks and calls
 *     window.flyToCartLaunch — unless window.flyToCartCardsBound says a full
 *     copy is handling the page already.
 *
 * What flies is a 10px dark dot (fly-to-cart.css), the same dot the PDP's
 * add-to-bag feedback launches (pdp.js launchAtbDot) — not the product image
 * tile of the Horizon original. So nothing here reads the card's image or
 * its [data-product-json]; the dot only needs the button and the bag.
 *
 * How it hooks in without touching the legacy handlers:
 *   - product-card.js binds click on .product__add / .product__add--mobile /
 *     .add-product-variant, and calls event.preventDefault() only after its
 *     product-data guard passes (i.e. the add really goes through AJAX). When
 *     the guard fails it lets the native form POST reload the page.
 *   - This document-level bubble listener runs right after that handler, in
 *     the same tick, so `event.defaultPrevented` tells us whether to animate,
 *     and the dot is created before the AJAX round-trip, not after it.
 *
 * Styles: assets/fly-to-cart.css. Recipe: FLY-TO-CART.md (repo root).
 */
(function () {
  'use strict';

  /* Read while this script element executes; each include carries its own
     currentScript, so a launcher-only copy never switches the card listener
     off for a full copy loaded on the same page. */
  var CARDS_OFF = !!(
    document.currentScript &&
    document.currentScript.getAttribute('data-fly-cards') === 'off'
  );

  var TRIGGER = '.product-card [data-add-to-cart], .product-card .add-product-variant';
  /* Adds fired from inside an overlay (legacy side-cart upsell cards, any
     open dialog) would fly out of the overlay to the header: skip those. */
  var EXCLUDE = '#side-cart, dialog, .cart-drawer-modal';
  /* Both the desktop (.shd) and mobile (.shm) header bags are in the DOM at
     once; CSS shows one per breakpoint. Legacy header section is the fallback. */
  var DESTINATIONS = ['.shd__bag .bag-icon', '.shm__bag .bag-icon', '.header__btn--cart'];
  var FLIGHT_MS = 600;
  var SAFETY_MS = 1500;

  function isEnabled() {
    if (!window.customElements || !window.HTMLElement) return false;
    var settings = window.theme && window.theme.settings;
    /* undefined (setting not saved yet) counts as enabled */
    if (settings && settings.enable_fly_to_cart === false) return false;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return false;
    }
    return true;
  }

  function isVisible(el) {
    if (!el || !el.getClientRects().length) return false;
    var rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function findDestination() {
    for (var i = 0; i < DESTINATIONS.length; i++) {
      var candidates = document.querySelectorAll(DESTINATIONS[i]);
      for (var j = 0; j < candidates.length; j++) {
        if (isVisible(candidates[j])) return candidates[j];
      }
    }
    return null;
  }

  function yieldToMainThread() {
    return new Promise(function (resolve) {
      requestAnimationFrame(function () {
        setTimeout(resolve, 0);
      });
    });
  }

  /* Measures source/destination, writes the CSS variables the keyframes read,
     removes itself when the animations finish. Plain class body (no class
     fields / #private) so an older parser fails on nothing here. */
  class FlyToCart extends HTMLElement {
    connectedCallback() {
      this.fly();
    }
  }

  FlyToCart.prototype.fly = function () {
    if (!this.source || !this.destination) {
      this.remove();
      return;
    }
    var sourceRect = this.source.getBoundingClientRect();
    var destinationRect = this.destination.getBoundingClientRect();
    if (!sourceRect.width || !destinationRect.width) {
      this.remove();
      return;
    }

    var start = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2
    };
    var end = {
      x: destinationRect.left + destinationRect.width / 2,
      /* Non-sticky header scrolled far above: exit just past the top edge
         instead of streaking thousands of pixels in 0.6s. */
      y: Math.max(destinationRect.top + destinationRect.height / 2, -40)
    };

    this.style.setProperty('--start-x', start.x + 'px');
    this.style.setProperty('--start-y', start.y + 'px');
    this.style.setProperty('--travel-x', end.x - start.x + 'px');
    this.style.setProperty('--travel-y', end.y - start.y + 'px');

    var self = this;
    var safety = setTimeout(function () {
      self.remove();
    }, FLIGHT_MS + SAFETY_MS);

    yieldToMainThread().then(function () {
      var animations = typeof self.getAnimations === 'function' ? self.getAnimations() : [];
      if (!animations.length) return null;
      return Promise.allSettled(animations.map(function (animation) {
        return animation.finished;
      }));
    }).then(function () {
      clearTimeout(safety);
      self.remove();
    });
  };

  /* Exposed as window.flyToCartLaunch for add-to-bag paths the click listener
     below does not cover — the XGen recommendations widget's cards live in a
     shadow root, and the native recommendation blocks sit on pages where the
     listener is deliberately not bound. The toggle and destination gates stay
     in here so every caller gets them. */
  function launch(button) {
    if (!button || button.disabled || !isEnabled()) return;

    var destination = findDestination();
    if (!destination) return;

    var dot = document.createElement('fly-to-cart');
    dot.classList.add('fly-to-cart--quick');
    dot.style.setProperty('--start-opacity', '0');
    dot.source = button;
    dot.destination = destination;
    document.body.appendChild(dot);
  }

  function onClick(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    var button = target.closest(TRIGGER);
    if (!button) return;
    /* No preventDefault => product-card.js fell back to the native form POST,
       the page is about to reload. */
    if (!event.defaultPrevented) return;
    if (button.closest(EXCLUDE)) return;
    launch(button);
  }

  if (!window.customElements || !window.Promise || !Promise.allSettled) return;

  if (!customElements.get('fly-to-cart')) {
    customElements.define('fly-to-cart', FlyToCart);
  }
  window.flyToCartLaunch = launch;
  /* Guarded so two includes on one page (none today) bind one listener. */
  if (!CARDS_OFF && !window.flyToCartCardsBound) {
    window.flyToCartCardsBound = true;
    document.addEventListener('click', onClick);
  }
})();
